import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate } from "@/lib/format";
import { Field } from "@/components/Common";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EQUIP_KINDS } from "@/components/fleet/fleetConst";
import { DatePicker } from "@/components/DatePicker";
import { Handover } from "@/components/personnel/Handover";
import { downloadFile } from "@/lib/download";
import { WorkEmail } from "@/components/personnel/WorkEmail";
import { LoanSummary } from "@/components/personnel/LoanSummary";


const KEYS = ["matricola", "tax_code", "qualifica", "job_title", "contract_type", "ccnl", "level", "weekly_hours", "hire_date", "contract_end", "termination_date", "termination_reason", "hourly_cost", "notes"];
const LABELS = { matricola: "Matricola", tax_code: "Codice fiscale", qualifica: "Qualifica", job_title: "Mansione", contract_type: "Tipo contratto", ccnl: "CCNL", level: "Livello",
  weekly_hours: "Ore settimanali", hire_date: "Data assunzione", contract_end: "Scadenza contratto", termination_date: "Data cessazione", termination_reason: "Motivo cessazione", hourly_cost: "Costo orario (€)", notes: "Note" };
const DATES = ["hire_date", "contract_end", "termination_date"];

const Section = ({ title, children }) => <div className="mt-6"><div className="micro-label mb-2 border-b pb-1">{title}</div>{children}</div>;

const Certs = ({ emp, meta, canManage, onChanged }) => {
  const EMPTY = { kind: "PES", issued_on: "", expiry: "", notes: "" };
  const [n, setN] = useState(EMPTY);
  const add = async () => {
    try { await api.post("/personnel/certs", { ...n, employee_id: emp.id, issued_on: n.issued_on || null, expiry: n.expiry || null }); toast.success("Abilitazione aggiunta"); setN(EMPTY); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => { try { await api.delete(`/personnel/certs/${c.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Section title="Abilitazioni, formazione e visite mediche">
      {emp.certs.length === 0 && <p className="text-sm text-muted-foreground">Nessuna registrata</p>}
      {emp.certs.map((c) => (
        <div key={c.id} className="flex items-center justify-between border-b py-1.5 text-sm" data-testid={`cert-row-${c.id}`}>
          <span>{meta.certs[c.kind]} {c.issued_on && <span className="text-muted-foreground">dal {fmtDate(c.issued_on)}</span>}</span>
          <span className={`font-mono text-xs ${c.expiry && c.expiry < today ? "font-bold text-red-700" : ""}`}>{c.expiry ? `scade ${fmtDate(c.expiry)}` : "senza scadenza"}
            {canManage && <Button size="icon" variant="ghost" onClick={() => del(c)} data-testid={`cert-delete-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</span>
        </div>
      ))}
      {canManage && (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <select className="h-9 border bg-white px-2 text-sm" value={n.kind} onChange={(e) => setN({ ...n, kind: e.target.value })} data-testid="cert-kind">{Object.entries(meta.certs).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <DatePicker compact clearable className="h-9" value={n.issued_on} onChange={(v) => setN({ ...n, issued_on: v })} placeholder="Conseguita il" testId="cert-issued" />
          <DatePicker compact clearable className="h-9" value={n.expiry} onChange={(v) => setN({ ...n, expiry: v })} placeholder="Scadenza" testId="cert-expiry" />
          <Button size="sm" onClick={add} data-testid="cert-add-button"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
        </div>
      )}
    </Section>
  );
};

const Assets = ({ emp, canManage }) => {
  const { nameOf } = useData();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/personnel/employees/${emp.id}/assets`).then((r) => setA(r.data)).catch(() => {}); }, [emp.id]);
  if (!a) return null;
  return (
    <Section title="Dotazioni assegnate">
      <div className="space-y-1 text-sm" data-testid="employee-assets">
        {a.teams.length > 0 && <div>Squadre: {a.teams.map((t) => nameOf("teams", t)).join(", ")}</div>}
        {a.vehicles.map((v) => <div key={v.id}>Mezzo <strong className="font-mono">{v.plate}</strong> {v.model} {v.team_id ? "(della squadra)" : "(personale)"}</div>)}
        {a.vans.map((w) => <div key={w.id}>Furgone-magazzino: {w.name}</div>)}
        {a.equipment.map((e) => <div key={e.id}>{EQUIP_KINDS[e.kind]}: {e.name} {e.serial && <span className="font-mono text-xs">({e.serial})</span>} {e.assigned_at && <span className="text-xs text-muted-foreground">consegnato il {fmtDate(e.assigned_at)}</span>}</div>)}
        {!a.vehicles.length && !a.equipment.length && !a.vans.length && <p className="text-muted-foreground">Nessuna dotazione. Assegna mezzi in Flotta e telefoni, SIM, PC o attrezzature in Flotta → Attrezzature.</p>}
      </div>
      {canManage && <Handover emp={emp} />}
    </Section>
  );
};

export const EmployeeSheet = ({ emp, meta, onClose, onChanged }) => {
  const { can } = useAuth();
  const { nameOf } = useData();
  const [f, setF] = useState({});
  useEffect(() => { if (emp) setF(Object.fromEntries(KEYS.map((k) => [k, emp[k] ?? (k === "weekly_hours" ? 40 : k === "hourly_cost" ? 0 : "")]))); }, [emp]);
  const save = useCallback(async () => {
    const ct = meta.contracts[f.contract_type] ? f.contract_type : Object.keys(meta.contracts).find((c) => meta.contracts[c] === f.contract_type) || "IND";
    const body = { ...f, contract_type: ct, weekly_hours: Number(f.weekly_hours) || 0, hourly_cost: Number(f.hourly_cost) || 0 };
    DATES.forEach((k) => { body[k] = body[k] || null; });
    try { await api.put(`/personnel/employees/${emp.id}/contract`, body); toast.success("Contratto salvato"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  }, [f, emp, meta, onChanged]);
  const m = can("hr:manage");
  return (
    <Sheet open={!!emp} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="employee-sheet">
        {emp && meta && <>
          <SheetHeader><SheetTitle className="font-display text-2xl">{emp.last_name} {emp.first_name}</SheetTitle></SheetHeader>
          <div className="text-sm text-muted-foreground">{nameOf("companies", emp.company_id, "legal_name")} {emp.active === false && "· CESSATO"} {emp.anonymized_at && "· ANONIMIZZATO"}</div>
          {m && <Button size="sm" variant="outline" className="mt-3" onClick={() => downloadFile(`/privacy/employees/${emp.id}/export`, "dati_personali.zip")} data-testid="employee-gdpr-export"><FileDown className="mr-1 h-4 w-4" />Esporta dati personali (GDPR)</Button>}
          <WorkEmail emp={emp} onChanged={onChanged} />
          <Section title="Rapporto di lavoro">
            <div className="grid gap-3 sm:grid-cols-2">
              {KEYS.map((k) => (
                <div key={k} className={k === "notes" ? "sm:col-span-2" : ""}>
                  <Label className="micro-label">{LABELS[k]}</Label>
                  {k === "contract_type" ? (
                    <select disabled={!m} className="mt-1 h-9 w-full border bg-white px-2 text-sm" value={meta.contracts[f[k]] ? f[k] : Object.keys(meta.contracts).find((c) => meta.contracts[c] === f[k]) || "IND"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid="contract-type">{Object.entries(meta.contracts).map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select>
                  ) : DATES.includes(k) ? (
                    <div className="mt-1"><DatePicker compact clearable disabled={!m} className="h-9" value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })} testId={`contract-${k}`} /></div>
                  ) : <Input disabled={!m} type={["weekly_hours", "hourly_cost"].includes(k) ? "number" : "text"} className="mt-1 h-9" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contract-${k}`} />}
                </div>
              ))}
            </div>
            {m && <Button className="mt-3" onClick={save} data-testid="contract-save-button">Salva contratto</Button>}
          </Section>
          {can("accounting:read") && <LoanSummary emp={emp} />}
          {emp.detachment && <Section title="Distacco"><p className="text-sm">Da {emp.detachment.from_company} dal {fmtDate(emp.detachment.from)} al {fmtDate(emp.detachment.to)} {emp.detachment.type} {emp.detachment.pct && `${emp.detachment.pct}%`}</p></Section>}
          <Certs emp={emp} meta={meta} canManage={m} onChanged={onChanged} />
          {emp.documents && <Section title="Documenti">{emp.documents.map((d) => <div key={d.kind} className="flex justify-between border-b py-1 text-sm"><span>{meta.docs[d.kind]} {d.number}</span><span className="font-mono text-xs">{d.expiry ? fmtDate(d.expiry) : d.note}</span></div>)}</Section>}
          {emp.personal && <Section title="Dati personali (riservati)"><div className="grid grid-cols-2 gap-3"><Field label="Nascita" value={emp.personal.birth} /><Field label="Residenza" value={emp.personal.residence} /><Field label="Telefono" value={emp.personal.phone} /><Field label="Email privata (solo HR)" value={emp.personal.email} /><Field label="Titolo di studio" value={emp.education} /></div></Section>}
          <Assets emp={emp} canManage={m} />
        </>}
      </SheetContent>
    </Sheet>
  );
};
